import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AlertService,
    AppConfigService,
    ClaimService,
    DateParserFormatterService,
    ENDPOINTS_CONFIG,
    ErrorStateService,
    GlobalConstants,
    GuidService,
    LoggingService,
} from '@app-core';
import { Address, AddressType, Claim, ClaimUpdate, Country, Employer, Parent, Physician, State, Student } from '@app-models';
import { FieldErrorDirective, PhoneValidator, SignaturePadComponent, ZipCodeValidator } from '@app-shared';
import { NgbDateParserFormatter, NgbDatepickerModule, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FraudNoticesComponent } from 'app/fraud-notices/fraud-notices.component';
import { AddressComponent } from 'app/shared/components/address/address.component';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-parent-response',
    imports: [ReactiveFormsModule, FieldErrorDirective, NgxMaskDirective, NgbDatepickerModule, SignaturePadComponent, AddressComponent],
    providers: [{ provide: NgbDateParserFormatter, useClass: DateParserFormatterService }],
    templateUrl: './parent-response.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './parent-response.component.scss',
})
export class ParentResponseComponent implements OnInit, OnDestroy {
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly claimService = inject(ClaimService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly spinnerService = inject(NgxSpinnerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dateParser = inject(DateParserFormatterService);
    private readonly guidService = inject(GuidService);
    private readonly alertService = inject(AlertService);
    private readonly errorStateService = inject(ErrorStateService);
    private readonly modalService = inject(NgbModal);
    private claimSubscription: Subscription;
    claimTypes = GlobalConstants.CLAIM_TYPE;
    claimStatus = GlobalConstants.CLAIM_STATUS;
    claim: Claim | null = null;
    claimForm: FormGroup;
    originalFormValues: any;
    states: State[];
    filteredStates: State[];
    countries: Country[];
    stateCaption: string = 'State';
    postalCodeCaption: string = 'Zip code';
    showAddress2: boolean = false;
    showNewAddress2: boolean = false;
    minValidDate: NgbDateStruct | null;
    maxValidDate: NgbDateStruct | null;
    // if claim type
    settlementAuthorizationText: string; // = GlobalConstants.FORM_TEXT.MEDICAL_AUTHORIZATION;
    //SETTLEMENT_AUTHORIZATION;
    authorizationText: string;
    certificationText: string;
    instructionText: string = GlobalConstants.FORM_TEXT.INSTRUCTION;
    signatureText: string = GlobalConstants.FORM_TEXT.SIGNATURE_AKNOWLEDGEMENT;
    withdrawalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.WITHDRAWAL;
    dismissalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.DISMISSAL;

    characterCount: number = 0;
    isSaving: boolean = false;

    get claimFormControls() {
        return this.claimForm.controls;
    }

    ngOnInit(): void {
        this.fetchClaimData();
    }

    fetchClaimData(): void {
        this.states = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.states);
        this.countries = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.countries);
        this.minValidDate = this.getMinimumSeparationDate();
        this.maxValidDate = this.getMaximumSeparationDate();
        this.initializeClaimForm();

        this.claimSubscription = this.claimService.getCurrentClaim().subscribe((claim: Claim) => {
            if (claim) {
                this.claim = claim;
                this.setOptionalRequiredFields(claim);
                this.claimForm.patchValue(this.claim);
                this.updateCountryCaptions(this.claim.accountParent?.addresses?.[0]?.countryISO3 || 'USA');
                this.setVariableText(this.claim, this.claim.claimType);
            }
        });
    }

    populateTestForm() {
        // populate all fields created in initializeClaimForm using patchvalue
        this.claimForm.patchValue({
            parentSeparationDate: this.convertStringToNgbDate('2023-01-01'),
            parentSeparationReason: 'Job Loss',
            parentSignature: 'John Doe',
            accountStudent: {
                firstName: 'Jane',
                lastName: 'Doe',
            },
            parent: {
                firstName: 'John',
                lastName: 'Doe',
                phoneNumber: '123-456-7890',
            },
            parentAddress: {
                address1: '123 Main St',
                address2: 'Apt 4B',
                city: 'Anytown',
                stateAbbreviation: 'CA',
                countryISO3: 'USA',
                postalCode: '12345',
            },
            parentRelocationDate: this.convertStringToNgbDate('2023-01-01'),
            parentNewAddress: {
                address1: '456 Elm St',
                address2: 'Apt 2A',
                city: 'Othertown',
                stateAbbreviation: 'NY',
                postalCode: '67890',
                countryISO3: 'USA',
            },
            parentPhysician: {
                firstName: 'Dr. Smith',
                lastName: 'Johnson',
                emailAddress: 'cartis@obitest.com',
                phoneNumber: '987-654-3210',
            },
            parentEmployer: {
                organizationName: 'ABC Corp',
                emailAddress: 'cartis@obitest.com',
                phoneNumber: '555-123-4567',
            },
        });
    }
    // If you have a date string like "2024-03-15" from your API:
    private convertStringToNgbDate(dateString: string): NgbDateStruct | null {
        if (!dateString) return null;

        const parts = dateString.split('-');
        return {
            year: Number.parseInt(parts[0], 10),
            month: Number.parseInt(parts[1], 10),
            day: Number.parseInt(parts[2], 10),
        };
    }

    setVariableText(claim: Claim, claimType: string) {
        if (this.claim.claimType === this.claimTypes.MEDICAL_WITHDRAWAL || this.claim.claimType === this.claimTypes.MEDICAL_ABSENCE) {
            this.settlementAuthorizationText = GlobalConstants.FORM_TEXT.MEDICAL_AUTHORIZATION;
        } else if (claimType === this.claimTypes.JOB_RELOCATION) {
            this.settlementAuthorizationText = GlobalConstants.FORM_TEXT.PARENT_JOB_RELOCATION_AUTHORIZATION;
            this.certificationText = `${GlobalConstants.FORM_TEXT.PARENT_JOB_RELOCATION_CERTIFICATION.replace('RELOCMILES', '<span class="fw-bold">' + claim.jobRelocationMileage + ' miles</span>')}`;
        } else if (claimType === this.claimTypes.INVOLUNTARY_UNEMPLOYMENT) {
            this.settlementAuthorizationText = GlobalConstants.FORM_TEXT.PARENT_UNEMPLOYMENT_AUTHORIZATION;
            this.certificationText = GlobalConstants.FORM_TEXT.PARENT_UNEMPLOYMENT_CERTIFICATION;
        } else {
            this.settlementAuthorizationText = GlobalConstants.FORM_TEXT.SETTLEMENT_AUTHORIZATION;
            this.authorizationText = GlobalConstants.FORM_TEXT.AUTHORIZATION;
        }
    }
    initializeClaimForm() {
        this.claimForm = this.formBuilder.group({
            parentSeparationDate: [''],
            parentSeparationReason: [''],
            parentSignature: ['', Validators.required],
            accountStudent: this.formBuilder.group({
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
            }),
            parent: this.formBuilder.group({
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
                phoneNumber: ['', [Validators.required, PhoneValidator.phone()]],
            }),

            parentAddress: this.formBuilder.group({
                address1: ['', [Validators.required, Validators.maxLength(200)]],
                address2: ['', [Validators.maxLength(200)]],
                city: ['', [Validators.required, Validators.maxLength(100)]],
                stateAbbreviation: ['', Validators.required],
                countryISO3: ['USA', Validators.required],
                postalCode: ['', [Validators.required, Validators.maxLength(10), ZipCodeValidator.validate('USA')]],
            }),
            parentRelocationDate: [''],
            parentNewAddress: this.formBuilder.group({
                address1: ['', [Validators.maxLength(200)]],
                address2: ['', [Validators.maxLength(200)]],
                city: ['', [Validators.maxLength(100)]],
                stateAbbreviation: [''],
                countryISO3: ['USA'],
                postalCode: ['', [Validators.maxLength(10)]],
            }),

            parentPhysician: this.formBuilder.group({
                firstName: ['', [Validators.maxLength(50)]],
                lastName: ['', [Validators.maxLength(50)]],
                emailAddress: ['', [Validators.maxLength(255), Validators.email]],
                phoneNumber: [''],
            }),
            parentEmployer: this.formBuilder.group({
                organizationName: ['', [Validators.maxLength(255)]],
                emailAddress: ['', [Validators.maxLength(255), Validators.email]],
                phoneNumber: [''],
            }),
        });
    }

    setOptionalRequiredFields(claim: Claim) {
        //
        if (claim.claimType === this.claimTypes.MEDICAL_WITHDRAWAL || claim.claimType === this.claimTypes.MEDICAL_ABSENCE) {
            this.claimForm.get('parentPhysician.firstName')?.setValidators([Validators.required, Validators.maxLength(50)]);
            this.claimForm.get('parentPhysician.lastName')?.setValidators([Validators.required, Validators.maxLength(50)]);
            this.claimForm.get('parentPhysician.phoneNumber')?.setValidators([Validators.required, PhoneValidator.phone()]);
            // Add max length validator for email address (not required by spec for required, but length constraint needed)
            this.claimForm.get('parentPhysician.emailAddress')?.setValidators([Validators.maxLength(255), Validators.email]);

            this.claimForm.get('parentPhysician.firstName')?.updateValueAndValidity();
            this.claimForm.get('parentPhysician.lastName')?.updateValueAndValidity();
            this.claimForm.get('parentPhysician.phoneNumber')?.updateValueAndValidity();
            this.claimForm.get('parentPhysician.emailAddress')?.updateValueAndValidity();
        } else if (claim.claimType === this.claimTypes.INVOLUNTARY_UNEMPLOYMENT) {
            this.claimForm.get('parentEmployer.organizationName')?.setValidators([Validators.required, Validators.maxLength(255)]);
            this.claimForm.get('parentEmployer.organizationName')?.updateValueAndValidity();
            this.claimForm.get('parentEmployer.emailAddress')?.setValidators([Validators.maxLength(255), Validators.email]);
            this.claimForm.get('parentEmployer.emailAddress')?.updateValueAndValidity();
        } else if (claim.claimType === this.claimTypes.JOB_RELOCATION) {
            this.claimForm.get('parentEmployer.organizationName')?.setValidators([Validators.required, Validators.maxLength(255)]);
            this.claimForm.get('parentEmployer.emailAddress')?.setValidators([Validators.maxLength(255), Validators.email]);
            this.claimForm.get('parentNewAddress.address1')?.setValidators([Validators.required, Validators.maxLength(200)]);
            this.claimForm.get('parentNewAddress.address2')?.setValidators([Validators.maxLength(200)]);
            this.claimForm.get('parentNewAddress.city')?.setValidators([Validators.required, Validators.maxLength(100)]);
            this.claimForm.get('parentNewAddress.stateAbbreviation')?.setValidators([Validators.required]);
            this.claimForm.get('parentNewAddress.countryISO3')?.setValidators([Validators.required]);
            this.claimForm.get('parentNewAddress.postalCode')?.setValidators([Validators.required, Validators.maxLength(10), ZipCodeValidator.validate('USA')]);
            this.claimForm.get('parentRelocationDate')?.setValidators([Validators.required]);

            this.claimForm.get('parentEmployer.organizationName')?.updateValueAndValidity();
            this.claimForm.get('parentEmployer.emailAddress')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.address1')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.address2')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.city')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.stateAbbreviation')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.countryISO3')?.updateValueAndValidity();
            this.claimForm.get('parentNewAddress.postalCode')?.updateValueAndValidity();
            this.claimForm.get('parentRelocationDate')?.updateValueAndValidity();
        } else {
            this.claimForm.addControl('parentSeparationDate', this.formBuilder.control('', Validators.required));
            this.claimForm.addControl('parentSeparationReason', this.formBuilder.control('', Validators.required));

            this.claimForm.get('parentSeparationDate')?.updateValueAndValidity();
            this.claimForm.get('parentSeparationReason')?.updateValueAndValidity();
        }
    }

    toggleAddress2() {
        this.showAddress2 = !this.showAddress2;
    }

    toggleNewAddress2() {
        this.showNewAddress2 = !this.showNewAddress2;
    }

    onCountryChange(event: any) {
        const countryISO3 = event.target.value;
        this.updateCountryCaptions(countryISO3);
        this.applyZipCodeValidator(countryISO3);
    }

    onSubmit() {
        this.isSaving = true;
        this.markFormGroupTouched(this.claimForm);

        if (this.claimForm.valid) {
            const claimData = this.buildClaimData();
            this.claimService.updateClaim(claimData, this.claim.id).subscribe({
                next: (response) => {
                    LoggingService.log('Application Updated', response);
                    this.claimService.setSubmissionSuccessful(true);
                    this.isSaving = false;
                    // route to success-medical and show success alert if claims type is medical else route to success
                    if (this.claim?.claimType === this.claimTypes.MEDICAL_ABSENCE || this.claim?.claimType === this.claimTypes.MEDICAL_WITHDRAWAL) {
                        const heading = `Claim ${this.claim.claimReferenceNumber.toString()} has been successfully submitted.`;
                        const message = 'Please note that a claim is not considered complete until the physician claim form has been submitted.';
                        this.alertService.success(message, { heading: heading, keepAfterRouteChange: true });
                    }
                    if (this.claim?.claimType === this.claimTypes.INVOLUNTARY_UNEMPLOYMENT || this.claim?.claimType === this.claimTypes.JOB_RELOCATION) {
                        const heading = `Claim ${this.claim.claimReferenceNumber.toString()} has been successfully submitted.`;
                        const message = 'Please note that a claim is not considered complete until the employer claim form has been submitted.';
                        this.alertService.success(message, { heading: heading, keepAfterRouteChange: true });
                    }

                    //const route =
                    //    this.claim?.claimType === this.claimTypes.MEDICAL_ABSENCE || this.claim?.claimType === this.claimTypes.MEDICAL_WITHDRAWAL ? '/success-medical' : '/success';
                    // Determine route based on claim type
                    let route = '/success';
                    if (this.claim?.claimType === this.claimTypes.MEDICAL_ABSENCE || this.claim?.claimType === this.claimTypes.MEDICAL_WITHDRAWAL) {
                        route = '/success-medical';
                    } else if (this.claim?.claimType === this.claimTypes.JOB_RELOCATION || this.claim?.claimType === this.claimTypes.INVOLUNTARY_UNEMPLOYMENT) {
                        route = '/success-employer';
                    }

                    this.router.navigate([route], { skipLocationChange: true });
                },
                error: (error) => {
                    this.claimService.setSubmissionSuccessful(false);
                    this.isSaving = false;
                    LoggingService.error('Error Updating Application', error);
                },
            });
        } else {
            // Reset isSaving if form is invalid
            this.isSaving = false;
            // Scroll to and focus the first invalid field
            this.scrollToFirstInvalidField();
        }
        this.originalFormValues = this.claimForm.value;
    }

    updateCharacterCount(event: Event): void {
        const input = event.target as HTMLTextAreaElement;
        this.characterCount = input.value.length;
    }

    fraudNotice() {
        this.modalService.open(FraudNoticesComponent, { scrollable: true, size: 'xl', centered: true, fullscreen: 'sm', windowClass: 'fraud-notices-modal' });
    }

    private buildClaimData(): ClaimUpdate {
        const claimUpdate = new ClaimUpdate();
        claimUpdate.id = this.claim.id;
        claimUpdate.parentSeparationDate = this.dateParser.format(this.claimForm.get('parentSeparationDate').value);
        claimUpdate.parentSeparationReason = this.claimForm.get('parentSeparationReason').value;
        claimUpdate.parentSignature = this.claimForm.get('parentSignature').value;
        claimUpdate.parentRelocationDate = this.dateParser.format(this.claimForm.get('parentRelocationDate').value);

        const parentStudent = new Student();
        parentStudent.firstName = this.claimForm.get('accountStudent.firstName').value;
        parentStudent.lastName = this.claimForm.get('accountStudent.lastName').value;
        claimUpdate.parentStudent = parentStudent;

        const parent = new Parent();
        parent.addresses = [];
        parent.firstName = this.claimForm.get('parent.firstName').value;
        parent.lastName = this.claimForm.get('parent.lastName').value;
        parent.phoneNumber = this.claimForm.get('parent.phoneNumber').value;

        const parentAddress = new Address();
        parentAddress.address1 = this.claimForm.get('parentAddress.address1').value;
        parentAddress.address2 = this.claimForm.get('parentAddress.address2').value;
        parentAddress.city = this.claimForm.get('parentAddress.city').value;
        parentAddress.stateAbbreviation = this.claimForm.get('parentAddress.stateAbbreviation').value;
        parentAddress.countryISO3 = this.claimForm.get('parentAddress.countryISO3').value;
        parentAddress.postalCode = this.claimForm.get('parentAddress.postalCode').value;
        const addressTypeOriginal = new AddressType();
        if (this.claim.claimType === this.claimTypes.INVOLUNTARY_UNEMPLOYMENT || this.claim.claimType === this.claimTypes.JOB_RELOCATION) {
            addressTypeOriginal.id = GlobalConstants.ADDRESS_TYPES.ORIGINAL_PERSON_ADDRESS;
        } else {
            addressTypeOriginal.id = GlobalConstants.ADDRESS_TYPES.MAILING_ADDRESS;
        }
        addressTypeOriginal.description = '';
        parentAddress.addressType = addressTypeOriginal;
        parent.addresses.push(parentAddress);
        claimUpdate.parent = parent;

        if (this.claim.claimType === this.claimTypes.JOB_RELOCATION) {
            const newAddress = new Address();
            newAddress.address1 = this.claimForm.get('parentNewAddress.address1').value;
            newAddress.address2 = this.claimForm.get('parentNewAddress.address2').value;
            newAddress.city = this.claimForm.get('parentNewAddress.city').value;
            newAddress.stateAbbreviation = this.claimForm.get('parentNewAddress.stateAbbreviation').value;
            newAddress.countryISO3 = this.claimForm.get('parentNewAddress.countryISO3').value;
            newAddress.postalCode = this.claimForm.get('parentNewAddress.postalCode').value;
            const addressTypeNew = new AddressType();
            addressTypeNew.id = GlobalConstants.ADDRESS_TYPES.NEW_PERSON_ADDRESS;
            addressTypeNew.description = '';
            newAddress.addressType = addressTypeNew;
            parent.addresses.push(newAddress);
        }

        const parentPhysician = new Physician();
        parentPhysician.firstName = this.claimForm.get('parentPhysician.firstName').value;
        parentPhysician.lastName = this.claimForm.get('parentPhysician.lastName').value;
        parentPhysician.emailAddress = this.claimForm.get('parentPhysician.emailAddress').value;
        parentPhysician.phoneNumber = this.claimForm.get('parentPhysician.phoneNumber').value;
        claimUpdate.parentPhysician = parentPhysician;

        const parentEmployer = new Employer();
        parentEmployer.organizationName = this.claimForm.get('parentEmployer.organizationName').value;
        parentEmployer.emailAddress = this.claimForm.get('parentEmployer.emailAddress').value;
        parentEmployer.phoneNumber = this.claimForm.get('parentEmployer.phoneNumber').value;
        claimUpdate.parentEmployer = parentEmployer;

        LoggingService.log(claimUpdate);
        return claimUpdate;
    }

    private getMinimumSeparationDate(): NgbDateStruct {
        return { day: 1, month: 1, year: 2020 };
    }

    private getMaximumSeparationDate(): NgbDateStruct {
        return { day: 31, month: 12, year: 2050 };
    }

    private updateCountryCaptions(countryISO3: string) {
        if (countryISO3) {
            this.stateCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].stateCaption;
            this.postalCodeCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].postalCodeCaption;
            this.filteredStates = this.states.filter((state) => state.country.iso3 === countryISO3).sort((a, b) => a.stateAbbreviation.localeCompare(b.stateAbbreviation));
            this.changeDetector.detectChanges();
        }
    }

    private scrollToFirstInvalidField() {
        // Find the first invalid form control (input, select, textarea)
        const firstInvalidControl = document.querySelector('input.ng-invalid, textarea.ng-invalid, select.ng-invalid, app-signature-pad.ng-invalid');

        if (firstInvalidControl) {
            // Scroll to the control
            firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Set focus on the element
            if (firstInvalidControl instanceof HTMLElement) {
                setTimeout(() => {
                    // For standard form controls
                    if (firstInvalidControl instanceof HTMLInputElement || firstInvalidControl instanceof HTMLTextAreaElement || firstInvalidControl instanceof HTMLSelectElement) {
                        firstInvalidControl.focus();
                    }
                    // For custom components like signature-pad
                    else if (firstInvalidControl.tagName.toLowerCase() === 'app-signature-pad') {
                        // Find focusable element inside the custom component
                        const canvas = firstInvalidControl.querySelector('canvas');
                        if (canvas) {
                            canvas.focus();
                        }
                    }
                }, 500); // Small delay to ensure the scroll completes first
            }
        }
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        }
    }

    private applyZipCodeValidator(countryISO3: string) {
        const postalCodeControl = this.claimForm.get('accountParent.address.postalCode');
        if (postalCodeControl) {
            postalCodeControl.setValidators([Validators.required, ZipCodeValidator.validate(countryISO3)]);
            postalCodeControl.markAsTouched();
            postalCodeControl.updateValueAndValidity();
        }
    }

    ngOnDestroy(): void {
        if (this.claimSubscription) {
            this.claimSubscription.unsubscribe();
        }
    }
}
